import { useRef } from 'react';
import { mergeProps, useFocusRing, useRadio, VisuallyHidden } from 'react-aria';
import { type RadioGroupState } from 'react-stately';

import { Glyph } from '../../common/Glyph';

import { theme } from '../../../theme/theme';
import styles from './SegmentedControlSegment.module.css';

export interface SegmentedControlOption {
  value: string;
  label: string;
  glyphSource?: string;
}

interface SegmentedControlSegmentProps {
  option: SegmentedControlOption;
  state: RadioGroupState;
}

export function SegmentedControlSegment(props: SegmentedControlSegmentProps) {
  const inputRef = useRef<HTMLInputElement>(null);
  const radio = useRadio(
    { value: props.option.value, children: props.option.label },
    props.state,
    inputRef,
  );
  const focusRing = useFocusRing();

  return (
    // labelProps cancel the label's native click. Chromium and Safari forward that click to the
    // input as one that react-aria takes for a screen reader, which shows the focus ring.
    <label
      {...radio.labelProps}
      className={styles.root}
      data-is-selected={radio.isSelected}
      data-is-disabled={radio.isDisabled}
      data-is-focus-visible={focusRing.isFocusVisible}
    >
      <VisuallyHidden>
        <input {...mergeProps(radio.inputProps, focusRing.focusProps)} ref={inputRef} />
      </VisuallyHidden>

      {props.option.glyphSource != null && (
        <Glyph
          source={props.option.glyphSource}
          size={theme.icon.size_default}
          color={'currentColor'}
        />
      )}

      <span className={styles.label}>{props.option.label}</span>
    </label>
  );
}
